import React, { useEffect, useMemo, useState } from 'react';
import { Button, Select, Typography, showToast } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import {
  dateShort,
  dayCount,
  fetchSalesTeamMembers,
  fetchScopeRecord,
  mapSalesTeam,
  patchScopeRecord,
  resendScopeInvite,
  STATE_UI
} from './api';
import { formatLocaleDate } from '../../Utils/HelperFunctions';
import { DetailViewStyles as S } from './styles';

const PILOT_LABEL = {
  open: 'Pilot · open',
  closed: 'Pilot · closed won',
  closed_won: 'Pilot · closed won',
  closed_lost: 'Pilot · closed lost'
};

const MetaItem = ({ label, value }) => (
  <S.MetaCol>
    <S.MetaLabel>{label}</S.MetaLabel>
    <S.MetaValue>{value}</S.MetaValue>
  </S.MetaCol>
);

const SidebarSection = ({ title, children, action }) => (
  <S.SidebarSection>
    <S.SidebarSectionHeader>
      <S.SidebarLabel>{title}</S.SidebarLabel>
      {action}
    </S.SidebarSectionHeader>
    {children}
  </S.SidebarSection>
);

const WaitingPanel = ({ email, sentAt, daysWaiting, expired }) => {
  const stale = !expired && daysWaiting != null && daysWaiting > 7;
  return (
    <S.WaitingCard>
      <S.WaitingTitleBlock>
        <Typography variant='subtitle1-medium'>
          {expired ? 'Invite link expired' : 'Waiting on the customer'}
        </Typography>
        <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.6, maxWidth: '70ch' }}>
          {expired ? (
            <>
              The link sent to {email}
              {daysWaiting != null ? ` ${daysWaiting}d ago` : ''} is no longer valid. Resend the invite, fill it in on a
              call, or fix the attribution below — the scope viewer stays empty until they submit on a live link.
            </>
          ) : (
            <>
              The link went to {email}
              {daysWaiting != null ? ` ${daysWaiting}d ago` : ''} and nothing has been submitted yet. Nudge them, fill
              it in on a call, or fix the attribution below — the scope viewer stays empty until they submit.
            </>
          )}
        </Typography>
      </S.WaitingTitleBlock>
      <S.WaitingStats>
        <S.WaitingStatCard>
          <S.MetaLabel>Sent</S.MetaLabel>
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            {dateShort(sentAt)}
          </Typography>
          <Typography variant='body5' color='#9E9E9E'>
            creating a link sends it immediately
          </Typography>
        </S.WaitingStatCard>
        <S.WaitingStatCard>
          <S.MetaLabel>{expired ? 'Expired' : 'Waiting'}</S.MetaLabel>
          <Typography variant='body1' sx={{ fontWeight: 600, color: expired || stale ? '#995C00' : '#1A1A1A' }}>
            {daysWaiting != null ? `${daysWaiting}d since sent` : '—'}
          </Typography>
          {(expired || stale) && (
            <Typography variant='body5' color='#9E9E9E'>
              {expired ? 'resend to give them a new link' : 'over a week — worth a nudge'}
            </Typography>
          )}
        </S.WaitingStatCard>
      </S.WaitingStats>
    </S.WaitingCard>
  );
};

const modifierValue = m => {
  if (m.modifier_type === 'subjective') return m.text_value || '—';
  return (m.selected_options || []).join(', ') || '—';
};

const hasModifierAnswer = m => {
  if (!m) return false;
  if (m.modifier_type === 'subjective') return !!(m.text_value || '').trim();
  return (m.selected_options || []).length > 0;
};

const scopeHasSelectedElements = sc => (sc.elements || []).some(el => el.selected);

const TradeCard = ({ trade, open, onToggle, compact }) => {
  const [expandedItems, setExpandedItems] = useState({});
  useEffect(() => {
    setExpandedItems({});
  }, [compact]);

  const sections = useMemo(
    () =>
      (trade.sections || [])
        .map(sec => ({
          ...sec,
          assigned_scopes: (sec.assigned_scopes || []).filter(scopeHasSelectedElements)
        }))
        .filter(sec => (sec.assigned_scopes || []).length > 0),
    [trade.sections]
  );
  const allScopes = sections.flatMap(s => s.assigned_scopes || []);
  const tradeMods = useMemo(() => (trade.trade_modifiers || []).filter(hasModifierAnswer), [trade.trade_modifiers]);
  const Header = open ? S.TradeHeaderOpen : S.TradeHeaderClosed;

  return (
    <S.TradeCard>
      <Header onClick={onToggle}>
        <S.TradeHeaderLeft>
          <Typography variant='body5' color='#9E9E9E'>
            {open ? '▾' : '▸'}
          </Typography>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {trade.trade_name}
          </Typography>
          <Typography variant='body4' color='#808080' sx={{ whiteSpace: 'nowrap' }}>
            {allScopes.length} scope{allScopes.length === 1 ? '' : 's'}
          </Typography>
        </S.TradeHeaderLeft>
        <Typography variant='body5' color='#9E9E9E' sx={{ whiteSpace: 'nowrap' }}>
          {sections.length} section{sections.length === 1 ? '' : 's'}
          {tradeMods.length > 0 ? ` · ${tradeMods.length} trade answer${tradeMods.length === 1 ? '' : 's'}` : ''}
        </Typography>
      </Header>

      {open && (
        <div>
          {sections.map(sec => {
            const scopes = sec.assigned_scopes || [];
            return (
              <S.TradeScopeBlock key={sec.section_id}>
                {sec.section_name && (
                  <Typography
                    variant='body5'
                    color='#9E9E9E'
                    sx={{ letterSpacing: '.04em', textTransform: 'uppercase', padding: '0 0 6px' }}>
                    {sec.section_name}
                  </Typography>
                )}
                {scopes.map(sc => {
                  const expanded = !compact || !!expandedItems[sc.id];
                  const elements = sc.elements || [];
                  const selected = elements.filter(e => e.selected);
                  const modifiers = (sc.modifiers || []).filter(hasModifierAnswer);
                  const Row = compact ? S.ScopeRowClickable : S.ScopeRowStatic;
                  return (
                    <S.ScopeRow key={sc.id}>
                      <Row
                        onClick={() => {
                          if (!compact) return;
                          setExpandedItems(p => ({ ...p, [sc.id]: !p[sc.id] }));
                        }}>
                        <S.ScopeRowText>
                          <Typography variant='body3' sx={{ fontWeight: 500 }}>
                            {sc.name}
                          </Typography>
                          <Typography variant='body4' color='#666666'>
                            {selected.length} of {elements.length} elements
                          </Typography>
                        </S.ScopeRowText>
                        {compact && <S.ScopeExpandHint>{expanded ? 'less' : 'more'}</S.ScopeExpandHint>}
                      </Row>
                      {expanded && (
                        <S.ScopeExpanded>
                          <S.Chips>
                            {selected.slice(0, 12).map(el => (
                              <S.Chip key={el.id}>{el.name}</S.Chip>
                            ))}
                          </S.Chips>
                          {modifiers.map(m => (
                            <S.KVLine key={m.id}>
                              <Typography variant='body4' color='#808080'>
                                {m.title}:
                              </Typography>
                              <Typography variant='body4' sx={{ fontWeight: 500 }}>
                                {modifierValue(m)}
                              </Typography>
                            </S.KVLine>
                          ))}
                        </S.ScopeExpanded>
                      )}
                    </S.ScopeRow>
                  );
                })}
              </S.TradeScopeBlock>
            );
          })}
          {trade.trade_note && (
            <S.TradeDetail>
              <Typography variant='body5' color='#9E9E9E' sx={{ letterSpacing: '.04em', textTransform: 'uppercase' }}>
                Trade note
              </Typography>
              <Typography variant='body4' color='#4D4D4D' sx={{ whiteSpace: 'pre-wrap' }}>
                {trade.trade_note}
              </Typography>
            </S.TradeDetail>
          )}
          {tradeMods.length > 0 && (
            <S.TradeDetail>
              <Typography variant='body5' color='#9E9E9E' sx={{ letterSpacing: '.04em', textTransform: 'uppercase' }}>
                Detailing &amp; deliverables
              </Typography>
              {tradeMods.map(m => (
                <S.KVLine key={m.id}>
                  <Typography variant='body4' color='#808080'>
                    {m.title}:
                  </Typography>
                  <Typography variant='body4' sx={{ fontWeight: 500 }}>
                    {modifierValue(m)}
                  </Typography>
                </S.KVLine>
              ))}
            </S.TradeDetail>
          )}
        </div>
      )}
    </S.TradeCard>
  );
};

const DetailView = ({ recordId, fromSection, onBack, onFillOnBehalf, onMutated }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attrEditing, setAttrEditing] = useState(false);
  const [attrDraft, setAttrDraft] = useState({ ae: '', fde: '' });
  const [attrSaving, setAttrSaving] = useState(false);
  const [team, setTeam] = useState({ ae: [], se: [] });
  const [viewMode, setViewMode] = useState('compact');
  const [openTrades, setOpenTrades] = useState({});
  const [reloadToken, setReloadToken] = useState(0);
  const [resending, setResending] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [unexpiring, setUnexpiring] = useState(false);
  const [pilotSaving, setPilotSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchScopeRecord(recordId).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        const msg = typeof err === 'string' ? err : err.detail || 'Failed to load record';
        showToast(msg, 'error');
        setRecord(null);
      } else {
        setRecord(data);
        setAttrDraft({ ae: data?.account_executive?.id || '', fde: data?.fde?.id || '' });
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [recordId, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    fetchSalesTeamMembers().then(({ data, error: err }) => {
      if (cancelled || err) return;
      setTeam(mapSalesTeam(data));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentVersion = record?.current_version || null;
  const hasScope = !!currentVersion;
  const { trades, tradesTotal } = useMemo(() => {
    const all = record?.trades_in_scope || [];
    return {
      tradesTotal: all.length,
      trades: all.filter(t =>
        (t.sections || []).some(sec =>
          (sec.assigned_scopes || []).some(sc => (sc.elements || []).some(el => el.selected))
        )
      )
    };
  }, [record]);

  const stateLabel = STATE_UI[String(record?.scope_form_status || '').toUpperCase()];
  const linkExpired = stateLabel === 'Sent · expired' || stateLabel === 'Reopened · expired';

  const sentAt = record?.sent_at || record?.created_at;
  const daysWaiting = useMemo(() => dayCount(sentAt), [sentAt]);

  const meta = useMemo(() => {
    if (!record) return [];
    const base = [
      { label: 'Customer email', value: record.customer_email },
      { label: 'Created', value: dateShort(record.created_at) },
      {
        label: 'Aging',
        value: daysWaiting != null ? `${daysWaiting}d since sent` : '—'
      }
    ];
    if (!hasScope) return base;
    return [...base, { label: 'Trades scoped', value: `${trades.length} of ${tradesTotal}` }];
  }, [record, hasScope, daysWaiting, trades.length, tradesTotal]);

  const attribution = record
    ? [
        { label: 'Prospect', value: record.company_name || '—' },
        { label: 'Creator', value: record.created_by?.name || '—' },
        { label: 'AE', value: record.account_executive?.name || '—' },
        { label: 'FDE', value: record.fde?.name || '—' },
        { label: 'CCs', value: record.cc_emails?.length ? `${record.cc_emails.length} recipients` : 'none' }
      ]
    : [];

  const changeLog = useMemo(() => {
    if (!record) return [];
    const entries = [];
    if (currentVersion) {
      entries.push({
        what: `Version v${currentVersion.version}${currentVersion.is_internal_submission ? ' · internal fill' : ''}`,
        who: `${currentVersion.actor_email || 'internal'} · ${formatLocaleDate(currentVersion.created_at)}`
      });
    }
    entries.push({
      what: `Link created and sent to ${record.customer_email}`,
      who: `${record.created_by?.name || 'creator'} · ${formatLocaleDate(record.created_at)}`
    });
    return entries;
  }, [record, currentVersion]);

  const backLabel = fromSection === 'records' ? '← All scope records' : '← All intake links';
  const pilotClosedLost = record?.pilot_status === 'closed_lost';

  const doPatch = async (body, successMsg, failMsg) => {
    const { data, error: err } = await patchScopeRecord(recordId, body);
    if (err) {
      showToast(failMsg || (typeof err === 'string' ? err : err.detail || 'Update failed'), 'error');
      return null;
    }
    if (successMsg) showToast(successMsg, 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
    return data;
  };

  const onSaveAttribution = async () => {
    setAttrSaving(true);
    const body = {};
    const currentAe = record.account_executive?.id || null;
    const currentFde = record.fde?.id || null;
    if ((attrDraft.ae || null) !== currentAe) body.account_executive_id = attrDraft.ae || null;
    if ((attrDraft.fde || null) !== currentFde) body.fde_id = attrDraft.fde || null;
    if (!Object.keys(body).length) {
      setAttrEditing(false);
      setAttrSaving(false);
      showToast('Nothing changed', 'info');
      return;
    }
    await doPatch(body, 'Attribution updated — recorded in the change history');
    setAttrEditing(false);
    setAttrSaving(false);
  };

  const onReopen = async () => {
    if (reopening) return;
    setReopening(true);
    await doPatch({ form_closed: false }, 'Reopened — the customer can submit on the same link');
    setReopening(false);
  };

  const onUnexpire = async () => {
    if (unexpiring) return;
    setUnexpiring(true);
    await doPatch({ unexpired: true }, 'Link unexpired — the customer can use it again');
    setUnexpiring(false);
  };

  const onMarkPilotLost = async () => {
    if (pilotSaving) return;
    setPilotSaving(true);
    const { data, error: err } = await patchScopeRecord(recordId, { pilot_status: 'closed_lost' });
    setPilotSaving(false);
    if (err) {
      const detail =
        typeof err === 'string'
          ? err
          : Array.isArray(err.pilot_status)
            ? err.pilot_status[0]
            : err.detail || 'Could not update pilot status.';
      showToast(detail, 'error');
      return;
    }
    showToast('Pilot marked as closed lost', 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  const onResend = async () => {
    if (!record?.id || resending) return;
    setResending(true);
    const { data, error } = await resendScopeInvite(record.id);
    setResending(false);
    if (error) {
      const detail = typeof error === 'string' ? error : error.error || error.detail || 'Could not resend the invite.';
      showToast(detail, 'error');
      return;
    }
    const msg = data?.message
      ? `${data.message} · ${record.customer_email}`
      : `Invite resent to ${record.customer_email || 'the customer'}`;
    showToast(msg, 'success');
  };

  const onCopyLink = () => {
    if (!record) return;
    const link = record.form_link || `${window.location.origin}/scope-form/${record.id}`;
    if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(link).catch(() => {});
    showToast(`Magic link for ${record.customer_email} copied`, 'success');
  };

  if (loading) {
    return <S.LoadingBox>Loading record…</S.LoadingBox>;
  }
  if (!record) {
    return (
      <S.ErrorWrap>
        <S.BackLink onClick={onBack}>{backLabel}</S.BackLink>
        <S.ErrorHint>Record could not be loaded — see the toast for details.</S.ErrorHint>
      </S.ErrorWrap>
    );
  }

  return (
    <S.Wrap>
      <S.BackLink onClick={onBack}>{backLabel}</S.BackLink>

      <S.Header>
        <S.HeaderLeft>
          <S.HeaderTitleRow>
            <S.H1>{record.company_name || record.customer_email}</S.H1>
            <StateLozenge state={stateLabel} />
            {record.pilot_status && (
              <S.PilotChip>{PILOT_LABEL[record.pilot_status] || record.pilot_status}</S.PilotChip>
            )}
          </S.HeaderTitleRow>
          <S.MetaRow>
            {meta.map(m => (
              <MetaItem key={m.label} label={m.label} value={m.value} />
            ))}
          </S.MetaRow>
        </S.HeaderLeft>

        <S.HeaderActions>
          {linkExpired && (
            <Button
              variant='contained'
              color='primary'
              size='medium'
              onClick={onUnexpire}
              loading={unexpiring}
              disabled={unexpiring || pilotClosedLost}>
              Unexpire
            </Button>
          )}
          {stateLabel === 'Reopened' ? null : stateLabel === 'Submitted' || hasScope ? (
            <Button
              variant='outlined'
              color='primary'
              size='medium'
              onClick={onReopen}
              loading={reopening}
              disabled={reopening || pilotClosedLost}>
              Reopen
            </Button>
          ) : (
            <Button
              variant='outlined'
              color='primary'
              size='medium'
              onClick={onResend}
              loading={resending}
              disabled={resending || pilotClosedLost}>
              Resend email
            </Button>
          )}
          {stateLabel !== 'Submitted' && (
            <Button
              variant='outlined'
              color='primary'
              size='medium'
              onClick={onFillOnBehalf}
              disabled={pilotClosedLost}>
              Fill on behalf
            </Button>
          )}
          {fromSection !== 'records' && !pilotClosedLost && (
            <Button
              variant='text'
              color='primary'
              size='medium'
              onClick={onMarkPilotLost}
              loading={pilotSaving}
              disabled={pilotSaving}>
              Mark pilot as closed lost
            </Button>
          )}
          <Button variant='text' color='primary' size='medium' onClick={onCopyLink} disabled={pilotClosedLost}>
            Copy link
          </Button>
        </S.HeaderActions>
      </S.Header>

      <S.BodyGrid>
        <S.Sidebar>
          {hasScope && currentVersion && (
            <>
              <S.SidebarLabel>Version history</S.SidebarLabel>
              <S.VersionCardOn>
                <S.VersionTopRow>
                  <Typography variant='body3' sx={{ fontWeight: 600 }}>
                    v{currentVersion.version}
                  </Typography>
                  <S.CurrentPill>current</S.CurrentPill>
                </S.VersionTopRow>
                <Typography variant='body4' color='#4D4D4D'>
                  {currentVersion.is_internal_submission ? 'Internal fill' : 'Customer submission'}
                </Typography>
                <Typography variant='body4' color='#808080'>
                  {currentVersion.actor_email || 'internal'} · {formatLocaleDate(currentVersion.created_at)}
                </Typography>
              </S.VersionCardOn>
            </>
          )}

          <SidebarSection
            title='Attribution'
            action={
              pilotClosedLost ? null : (
                <S.SidebarEditLink onClick={() => setAttrEditing(v => !v)}>
                  {attrEditing ? 'Cancel' : 'Edit'}
                </S.SidebarEditLink>
              )
            }>
            {attrEditing ? (
              <S.AttrEdit>
                <S.AttrEditField>
                  <Typography variant='body4' color='#808080'>
                    AE
                  </Typography>
                  <Select
                    size='small'
                    variant='std-filled'
                    items={team.ae}
                    value={attrDraft.ae}
                    onChange={v => setAttrDraft(d => ({ ...d, ae: v }))}
                  />
                </S.AttrEditField>
                <S.AttrEditField>
                  <Typography variant='body4' color='#808080'>
                    FDE
                  </Typography>
                  <Select
                    size='small'
                    variant='std-filled'
                    items={team.se}
                    value={attrDraft.fde}
                    onChange={v => setAttrDraft(d => ({ ...d, fde: v }))}
                  />
                </S.AttrEditField>
                <S.AttrEditActions>
                  <Button
                    variant='contained'
                    color='primary'
                    size='medium'
                    loading={attrSaving}
                    disabled={pilotClosedLost}
                    onClick={onSaveAttribution}>
                    Save changes
                  </Button>
                  <Button variant='text' color='primary' size='medium' onClick={() => setAttrEditing(false)}>
                    Cancel
                  </Button>
                </S.AttrEditActions>
              </S.AttrEdit>
            ) : (
              <div>
                {attribution.map(a => (
                  <S.AttrReadRow key={a.label}>
                    <Typography variant='body3' color='#808080'>
                      {a.label}
                    </Typography>
                    <Typography variant='body3' sx={{ fontWeight: 500, textAlign: 'right' }}>
                      {a.value}
                    </Typography>
                  </S.AttrReadRow>
                ))}
              </div>
            )}
          </SidebarSection>

          <SidebarSection title='Change history'>
            <S.ChangeLogList>
              {changeLog.map((c, i) => (
                <S.ChangeLogItem key={i}>
                  <Typography variant='body4' color='#4D4D4D' sx={{ lineHeight: 1.45 }}>
                    {c.what}
                  </Typography>
                  <Typography variant='body5' color='#9E9E9E'>
                    {c.who}
                  </Typography>
                </S.ChangeLogItem>
              ))}
            </S.ChangeLogList>
          </SidebarSection>
        </S.Sidebar>

        <S.Main>
          {!hasScope && (
            <WaitingPanel
              email={record.customer_email}
              sentAt={sentAt}
              daysWaiting={daysWaiting}
              expired={linkExpired}
            />
          )}

          {hasScope && (
            <>
              <S.ScopeToolbar>
                <div>
                  <S.ScopeTab>Scope</S.ScopeTab>
                </div>
                <S.ViewToggleWrap>
                  <Typography variant='body4' color='#808080' sx={{ whiteSpace: 'nowrap' }}>
                    {trades.length} trade{trades.length === 1 ? '' : 's'}
                  </Typography>
                  <S.ViewToggleGroup>
                    {['Compact', 'Full'].map(label => {
                      const value = label === 'Compact' ? 'compact' : 'full';
                      return (
                        <S.ViewToggle key={label} on={viewMode === value} onClick={() => setViewMode(value)}>
                          {label}
                        </S.ViewToggle>
                      );
                    })}
                  </S.ViewToggleGroup>
                </S.ViewToggleWrap>
              </S.ScopeToolbar>

              {trades.map((t, i) => (
                <TradeCard
                  key={t.trade_id}
                  trade={t}
                  compact={viewMode === 'compact'}
                  open={i === 0 ? openTrades[t.trade_id] !== false : !!openTrades[t.trade_id]}
                  onToggle={() =>
                    setOpenTrades(prev => {
                      if (i === 0) return { ...prev, [t.trade_id]: prev[t.trade_id] === false ? true : false };
                      return { ...prev, [t.trade_id]: !prev[t.trade_id] };
                    })
                  }
                />
              ))}
            </>
          )}
        </S.Main>
      </S.BodyGrid>
    </S.Wrap>
  );
};

export default DetailView;
